import type { PerformanceCustomerRow } from "@repo/app-types/entities/performanceCustomer";
import { asPlaybookId } from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type BasicTeammate,
} from "@repo/app-types/entities/teammate";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { NumberFormatter } from "@repo/ui/numbers/numberFormatter";
import { OutcomeResultPill } from "@repo/ui/Pills/outcomeResultPill";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { IconSearch } from "@tabler/icons-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { useNavigate, useParams } from "react-router";
import { useCustomersInfiniteQuery } from "../../api/entities/customer.js";
import { appPaths } from "../../app/appPaths.js";
import {
  PageBody,
  PageLayout,
  PageLayoutHeight,
  TableCard,
} from "../../components/layout/pageLayout.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { useFilters } from "../filters/filters.js";
import { ContactNameLines } from "../teammatePerformance/contactNameLines.js";

const columns: readonly EntityTableColumnDefinition<PerformanceCustomerRow>[] =
  [
    {
      id: "name",
      className: "w-1/3",
      header: "Customers",
      isRowHeader: true,
      getTextValue: (customer) =>
        `${customer.primary_display_name ?? ""} ${customer.secondary_display_name ?? ""}`.trim(),
      renderCell: (customer) => (
        <ContactNameLines
          primaryDisplayName={customer.primary_display_name ?? undefined}
          secondaryDisplayName={customer.secondary_display_name ?? undefined}
          isGeneratedFromTranscript={customer.is_generated_from_transcript}
        />
      ),
    },
    {
      id: "teammates",
      className: "w-1/3",
      header: "Teammates",
      getTextValue: (customer) =>
        buildTeammateNames(customer.teammates).join(", ") || "--",
      renderCell: (customer) => (
        <span className={PH_MASK_CLASS}>
          {renderTeammateSummary(customer.teammates)}
        </span>
      ),
    },
    {
      id: "outcome",
      header: "Outcome",
      className: "w-1/4",
      getTextValue: (customer) => customer.latest_outcome?.label ?? "--",
      renderCell: (customer) =>
        customer.latest_outcome ? (
          <OutcomeResultPill
            size={Size.SMALL}
            result={customer.latest_outcome}
          />
        ) : (
          <span className="text-muted-foreground">--</span>
        ),
    },
    {
      id: "calls",
      header: "Calls",
      className: "w-1/12",
      getTextValue: (customer) => String(customer.call_count),
      renderCell: (customer) => <NumberFormatter value={customer.call_count} />,
    },
  ];

function buildTeammateNames(teammates: readonly BasicTeammate[]): string[] {
  return teammates
    .map((teammate) => buildTeammateDisplayNames(teammate).primaryDisplayName)
    .toSorted((a, b) => a.localeCompare(b));
}

function renderTeammateSummary(teammates: readonly BasicTeammate[]): string {
  const names = buildTeammateNames(teammates);
  if (names.length === 0) return "--";
  const visibleNames = names.slice(0, 2);
  if (names.length > 2) visibleNames.push(`+${names.length - 2}`);
  return visibleNames.join(", ");
}

export const CustomersPage: FC = () => {
  const { playbookId: playbookIdParam } = useParams();
  const navigate = useNavigate();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId param.");
  }
  const playbookId = asPlaybookId(playbookIdParam);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [{ teammateGroupIds }, renderFilters] = useFilters([
    "teammateGroupIds",
  ]);
  useEffect(() => {
    const timeout = setTimeout(() => setAppliedSearch(search.trim()), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const {
    currentData,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCustomersInfiniteQuery({
    search: appliedSearch,
    teammateGroupIds: teammateGroupIds ?? null,
  });

  const customers = useMemo(
    () => currentData?.pages.flatMap((page) => page.customers) ?? [],
    [currentData],
  );

  return (
    <PageLayout height={PageLayoutHeight.Bounded}>
      <PageHeader
        title="Customers"
        filterBar={renderFilters({
          isLoading: isFetching && !isFetchingNextPage,
          leadingItems: (
            <div className="w-56 shrink-0">
              <Input
                accessibleLabel="Search customers"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onClear={() => setSearch("")}
                placeholder="Search customers…"
                leftIcon={<IconSearch className="size-4 text-gray-400" />}
                inputClassName={PH_MASK_CLASS}
                maxLength={100}
                size={Size.SMALL}
              />
            </div>
          ),
        })}
      />
      <PageBody fills>
        <TableCard>
          <EntityTable
            aria-label="Customers"
            columns={columns}
            entities={customers}
            onEntityAction={(customer) => {
              navigate(
                appPaths.playbookCustomer.build({
                  playbookId,
                  accountId: customer.id,
                }),
              );
            }}
            isLoading={isFetching && !isFetchingNextPage}
            isError={isError}
            onLoadMore={
              hasNextPage && !isFetchingNextPage ? fetchNextPage : undefined
            }
            emptyMessage="No customers found."
            size={Size.SMALL}
            isFullBleed
          />
        </TableCard>
      </PageBody>
    </PageLayout>
  );
};
